import { useEffect, useMemo, useState } from 'react';
import { getLiveProgram, programIsUpcoming } from '@jwp/ott-common/src/utils/epg';
import type { EpgChannel } from '@jwp/ott-common/types/epg';

/**
 * This hook fetches the schedules for the given list of playlist items and manages the current channel and program.
 *
 * It automatically selects the initial channel (or first channel) and currently live program. It also updates the
 * program information when the current program is not live anymore.
 *
 * The `enableAutoUpdate` argument can be used to ignore the auto update mechanism. For example, when playing a live
 * program from the beginning, we don't want to update the program information in the middle of the program.
 *
 * @todo The `enableAutoUpdate` mechanism has a drawback, when used the program information will not be updated anymore.
 *       Meaning that when watching a program from the beginning, it will hold the current program until the user clicks
 *       on a different program. This can be solved when we implement syncing the stream PDT with the schedule. Then the
 *       program information will be reactive based on the current time in the stream.
 */
const useLiveChannels = ({
  channels,
  channelId,
  programId,
  enableAutoUpdate = true,
}: {
  channels: EpgChannel[];
  channelId: string | undefined;
  programId: string | undefined;
  enableAutoUpdate?: boolean;
}) => {
  const [update, setUpdate] = useState(1);
  const [autoUpdate, setAutoUpdate] = useState(enableAutoUpdate);

  const { channel, program, liveProgram, nextProgram, upcomingProgram } = useMemo(() => {
    const channel = channels.find(({ id }) => id === channelId) || channels[0];
    const liveProgram = channel ? getLiveProgram(channel) : undefined;
    const upcomingProgram = channel?.programs.find(programIsUpcoming);
    const program = channel?.programs.find((program) => program.id === programId) || liveProgram;
    const baseProgram = program || upcomingProgram;
    const nextProgram = channel && baseProgram ? channel.programs.find((p) => p.startTime >= baseProgram.endTime) : undefined;

    return {
      channel,
      program,
      liveProgram,
      nextProgram,
      upcomingProgram,
    };

    // update variable is needed to re-evaluate liveProgram when auto updating
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId, channels, programId, update]);

  // this effect updates the program when watching the live stream and the next program starts
  useEffect(() => {
    if (!autoUpdate || !enableAutoUpdate) return;

    const intervalId = window.setInterval(() => setUpdate((current) => current + 1), 5_000);

    return () => clearInterval(intervalId);
  }, [channel, autoUpdate, enableAutoUpdate]);

  // enable auto update when there is no programId given
  useEffect(() => {
    setAutoUpdate(!programId);
  }, [programId]);

  return {
    channel,
    channels,
    program,
    liveProgram,
    nextProgram,
    upcomingProgram,
  };
};

export default useLiveChannels;
